import { memo, useState, useMemo } from 'react';

import { FiCalendar, FiMonitor } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { UseQueryResult } from 'react-query';
import { AxiosError } from 'axios';
import format from 'date-fns/format';
import isPast from 'date-fns/isPast';
import isFuture from 'date-fns/isFuture';
import { enUS, ptBR } from 'date-fns/locale';

import { FullScheduleData, FullScheduleResult } from '@common/types/api';
import { formatScheduleDisplayData } from '@utils';
import { HistoryState } from '@services/history';

import * as S from './styles';

type SchedulePanelProps = {
  scheduleQuery: UseQueryResult<FullScheduleResult, AxiosError>;
};

const isSessionActive = (session: FullScheduleData) => {
  if (!session.schedule_date || !session.start_time || !session.end_time) {
    return false;
  }
  const start = new Date(`${session.schedule_date}T${session.start_time}`);
  const end = new Date(`${session.schedule_date}T${session.end_time}`);
  return isPast(start) && isFuture(end);
};

const extractDays = (sessions: FullScheduleData[], locale: Locale) => {
  const uniqueDays: { date: Date; label: string; num: string }[] = [];
  const seen = new Set<string>();

  sessions.forEach((session) => {
    if (!session.schedule_date) return;
    const date = new Date(`${session.schedule_date}T00:00`);
    const key = session.schedule_date;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueDays.push({
        date,
        label: format(date, 'EEE', { locale }).substring(0, 3).toUpperCase(),
        num: format(date, 'dd'),
      });
    }
  });

  return uniqueDays.sort((a, b) => a.date.getTime() - b.date.getTime());
};

const TimelineEntry = ({
  session,
  active,
  showLine,
}: {
  session: FullScheduleData;
  active: boolean;
  showLine: boolean;
}) => {
  const { timetable } = formatScheduleDisplayData(session);
  const [time] = timetable.split(' - ');

  return (
    <>
      <S.DotColumn>
        <S.Dot $active={active}>{active && <S.PulseRing />}</S.Dot>
        {showLine && <S.Line $active={active} />}
      </S.DotColumn>

      <S.ContentColumn>
        <S.TitleRow>
          <S.TimeLabel $active={active}>{time}</S.TimeLabel>
          <S.TitleSeparator />
          <S.SessionTitle>{session.title}</S.SessionTitle>
          {active && <S.LiveChip>live</S.LiveChip>}
        </S.TitleRow>
        {session.room?.data?.name && (
          <S.RoomBadge>
            <FiMonitor size={10} />
            {session.room.data.name}
          </S.RoomBadge>
        )}
        {session.participants?.data?.length > 0 && (
          <S.SpeakerBubbles>
            {session.participants.data.slice(0, 3).map((speaker, idx) => (
              <S.SpeakerBubble key={speaker.id || idx} $index={idx}>
                <img src={speaker.photo} alt={speaker.name} />
              </S.SpeakerBubble>
            ))}
          </S.SpeakerBubbles>
        )}
      </S.ContentColumn>
    </>
  );
};

const SchedulePanel = ({ scheduleQuery }: SchedulePanelProps) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { pathname, search } = useLocation<HistoryState>();
  const locale = i18n.language === 'en' ? enUS : ptBR;

  const schedule = useMemo(
    () => scheduleQuery.data?.schedule || [],
    [scheduleQuery.data?.schedule],
  );

  const days = useMemo(() => extractDays(schedule, locale), [schedule, locale]);

  const [activeDay, setActiveDay] = useState(0);

  const filteredSessions = useMemo(() => {
    if (!days.length) return schedule.slice(0, 3);
    const selectedDate = days[activeDay]?.date;
    if (!selectedDate) return [];
    const selectedKey = format(selectedDate, 'yyyy-MM-dd');
    return schedule.filter((session) => session.schedule_date === selectedKey);
  }, [schedule, days, activeDay]);

  const handleSessionClick = (sessionId: number) => {
    const queryParams = new URLSearchParams(search);
    queryParams.set('session', `${sessionId}`);
    history.push({
      pathname,
      search: `?${queryParams.toString()}`,
    });
  };

  if (!scheduleQuery.isSuccess) {
    return null;
  }

  const visibleSessions = filteredSessions.slice(0, 3);

  if (!schedule.length) {
    return (
      <S.Container>
        <S.Header>
          <S.HeaderLeft>
            <FiCalendar size={14} />
            <h3>{t('pages.lobby.schedule')}</h3>
          </S.HeaderLeft>
        </S.Header>
        <S.EmptyState>
          <FiCalendar size={32} />
          <strong>Nenhuma programação ainda</strong>
          <span>Os eventos aparecerão aqui assim que forem publicados.</span>
        </S.EmptyState>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <S.Header>
        <S.HeaderLeft>
          <FiCalendar size={14} />
          <h3>{t('pages.lobby.schedule')}</h3>
        </S.HeaderLeft>
        <S.FullAgendaLink to="/schedule">Ver agenda completa</S.FullAgendaLink>
      </S.Header>

      {days.length > 0 && (
        <S.DaySelector>
          {days.map((d, i) => (
            <S.DayButton
              key={`day-${d.num}`}
              $active={activeDay === i}
              onClick={() => setActiveDay(i)}
            >
              <S.DayLabel>{d.label}</S.DayLabel>
              <S.DayNumber>{d.num}</S.DayNumber>
            </S.DayButton>
          ))}
        </S.DaySelector>
      )}

      {visibleSessions.map((session, i) => (
        <S.TimelineItem
          key={`schedule-${session.id}`}
          onClick={() => handleSessionClick(session.id)}
        >
          <TimelineEntry
            session={session}
            active={isSessionActive(session)}
            showLine={i < visibleSessions.length - 1}
          />
        </S.TimelineItem>
      ))}
    </S.Container>
  );
};

export default memo(SchedulePanel);
